'use client'

import { useCallback, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useVersionCheck } from '@/hooks/use-version-check'
import { hasPendingWork, safeRefresh } from '@/lib/safe-refresh'
import { BUILD_TIMESTAMP_KEY } from '@/lib/submission-cache'

/**
 * Per-tab record of every build this tab has already auto-refreshed AWAY from.
 * Same shape as `safe-refresh`'s own `RECOVERY_FLAG`: `sessionStorage`, so it
 * is scoped to the tab and cleared when the tab closes.
 */
const REFRESHED_FROM_KEY = 'cadence-version-watcher:refreshed-from'

/**
 * Upper bound on remembered builds. A tab that has genuinely seen this many
 * deploys is not the loop this guards against, and the cap keeps one
 * long-lived tab from growing the entry without limit.
 */
const MAX_REMEMBERED_BUILDS = 10

/**
 * VersionWatcher (#13889, replacing the RefreshBanner of #3781)
 *
 * The app-wide mount of `useVersionCheck`. It renders nothing, and that is the
 * change: its predecessor was an amber banner announcing a new build and
 * asking whether to refresh now or later — a question the reader has no basis
 * to answer — and it could be dismissed into permanent staleness. The client
 * now moves itself onto the new build at the next natural boundary instead.
 *
 * ## What it carries besides the refresh
 *
 * `useVersionCheck` has three consumers that have nothing to do with any
 * banner, and this component is the only place in the app that mounts it:
 *
 *  - it writes `BUILD_TIMESTAMP_KEY` into `sessionStorage`, which
 *    `src/lib/resilient-submit.ts` compares to detect a deploy at submit
 *    time — in context, where a stale client actually matters;
 *  - it dispatches `cadence:server-reachable` on every poll, which
 *    `src/lib/submission-recovery.ts` uses to replay queued submissions;
 *  - its `status` tells `src/components/timezone-drift-prompt.tsx` to hold
 *    its prompt while a reload is imminent.
 *
 * Deleting the banner without re-homing the hook would have killed all three
 * silently. `tests/unit/components/version-watcher.test.tsx` runs the real
 * hook against a stubbed `/api/health` so that regression is caught rather
 * than reasoned about.
 *
 * ## The two boundaries, and the gate they share
 *
 * A reload is safe at a moment the reader is not looking at the page — the tab
 * going hidden — and at a moment the page is being replaced anyway — a route
 * change. Tab-hidden alone (the inherited behaviour) leaves someone who keeps
 * one tab open all day accumulating staleness indefinitely, which is what the
 * banner used to paper over.
 *
 * Both are gated on `hasPendingWork()`. Two of its three categories survive a
 * reload on their own (localStorage drafts, and cached submissions that
 * `useSubmissionRecovery` replays), but the third — in-memory
 * `registerPendingWorkProvider` state — does not, so the gate stays until
 * those call sites flush their own state. `safeRefresh()` rather than a raw
 * reload so registered `beforeRefresh` callbacks still flush first.
 *
 * **The route-change reload lands after the destination has painted.** The
 * effect runs once the client-side navigation has committed, so a reader who
 * clicks a link on a stale tab sees the new route render and then be replaced
 * by a full document load. That is the deliberate trade — the alternative is
 * blocking navigation on a version check — but it is a visible artefact that
 * the word "silent" does not prepare anyone for, so: if you are triaging a
 * report that the app reloaded by itself right after a click, this is why.
 */
export function VersionWatcher() {
  const { status } = useVersionCheck()
  const pathname = usePathname()

  /**
   * The bound. Without it the auto-refresh can loop: `useVersionCheck`
   * re-baselines `initialBuildRef` on every fresh document from whatever
   * `/api/health` answers, so during a rolling deploy — old and new replicas
   * both serving — a client can reload, re-baseline against the OLD build,
   * poll the new one, flip to `stale` again, and reload at the next
   * navigation. The banner this component replaced degraded into an amber box
   * you could dismiss; unbounded, this degrades into an app that reloads on
   * every click, and #13889 deleted the only dismiss control there was.
   *
   * So record every build we refresh away from, and refuse to do it twice
   * from the same one in the same tab. The second attempt from a given build
   * is exactly the one that proves the reload did not help, which is the
   * loop's first repetition rather than its Nth.
   *
   * A SET rather than one slot, because two replicas alternating defeat a
   * single slot outright: A→B overwrites the slot with A, B→A overwrites it
   * with B, and A→B is permitted again forever. Per-build, that sequence
   * stops after each of A and B has had its one refresh.
   *
   * Storage is best-effort on purpose: if `sessionStorage` throws (Safari
   * private mode, blocked site data) the refresh still happens. Losing the
   * bound is the cheaper failure — the loop needs a rolling deploy to occur
   * at all, while refusing to refresh would strand every reader on a stale
   * build for the life of the tab.
   */
  const shouldAutoRefresh = useCallback(() => {
    try {
      const currentBuild = sessionStorage.getItem(BUILD_TIMESTAMP_KEY)
      if (currentBuild === null) return true

      // A malformed entry is treated as empty rather than fatal: the bound is
      // a safety net, and refusing to parse it must not refuse the refresh.
      let seen: string[] = []
      const raw = sessionStorage.getItem(REFRESHED_FROM_KEY)
      if (raw !== null) {
        try {
          const parsed: unknown = JSON.parse(raw)
          if (Array.isArray(parsed)) {
            seen = parsed.filter((b): b is string => typeof b === 'string')
          }
        } catch {
          // A corrupt entry is indistinguishable from none — see sub-issue R carve-out
        }
      }

      if (seen.includes(currentBuild)) return false

      sessionStorage.setItem(
        REFRESHED_FROM_KEY,
        JSON.stringify([...seen, currentBuild].slice(-MAX_REMEMBERED_BUILDS)),
      )
    } catch {
      // The throw IS the answer: storage is gone, so there is no bound to read — see sub-issue R carve-out
      //
      // Proceed unbounded rather than leaving the reader stranded on a stale
      // build for the life of the tab; see this callback's doc comment.
    }
    return true
  }, [])

  // Boundary 1: the tab goes hidden.
  useEffect(() => {
    if (status !== 'stale') return

    const handleVisibility = () => {
      if (
        document.visibilityState === 'hidden' &&
        !hasPendingWork() &&
        shouldAutoRefresh()
      ) {
        safeRefresh()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [status, shouldAutoRefresh])

  // Boundary 2: a route change.
  //
  // The ref is what makes this a route CHANGE rather than "the effect ran".
  // Without it, the render in which `status` first becomes `stale` would
  // reload the page out from under whoever is reading it — the interruption
  // this component exists to remove, in a louder form than the banner.
  const lastPathRef = useRef<string | null>(null)
  useEffect(() => {
    const previousPath = lastPathRef.current
    lastPathRef.current = pathname

    if (previousPath === null || previousPath === pathname) return
    if (status !== 'stale') return
    if (hasPendingWork()) return
    if (!shouldAutoRefresh()) return

    safeRefresh()
  }, [pathname, status, shouldAutoRefresh])

  return null
}
